import { getLocation, LOCATION_CHANGE, LocationChangeAction } from 'connected-react-router';
import { r } from '../../../routes';
import { startAppListening } from '../../../state/middlewares/listener';
import * as appUtils from '../../../utils/basics';
import { actions, isSheetOpen } from '../ducks';
import { GeneralFlowActionTypes } from '../typings';

export function scrollToTop(action: LocationChangeAction): void {
  if (action.payload.location.pathname.indexOf(r.generalFlow.index()) > -1) {
    window.scrollTo(0, 0);
  }
}

export function afterPushNotification(): void {
  // the error itself, not an info message that may sit above it
  const messageElement = document.querySelector<HTMLElement>('.message--error');
  if (messageElement) {
    messageElement.scrollIntoView();
    // first wait for scroll to the view and wait for update window scroll Y then scroll a bit to the top
    setTimeout(() => window.scroll(0, window.scrollY - 50), 100);
  }
}

/**
 * Registers the GeneralFlow side effects of navigation and of pushed notifications
 * (same behavior as the former LOCATION_CHANGE and PUSH_NOTIFICATION watchers of generalSaga).
 * The former takeLatest handlers are synchronous, so they are plain listeners, registered in the same order.
 */
export const startNavigationListeners = (): void => {
  /**
   * Scrolls to the top in the flow, once per burst of navigations (same behavior as the former scrollToTop saga).
   */
  startAppListening({
    type: LOCATION_CHANGE,
    effect: async (action, api) => {
      api.cancelActiveListeners();
      await api.delay(100);
      scrollToTop(action as LocationChangeAction);
    },
  });

  /**
   * Resets the price loading and the general error in the flow
   * (same behavior as the former resetLoadingNotificationState saga).
   */
  startAppListening({
    type: LOCATION_CHANGE,
    effect: (_action, api) => {
      try {
        const location = getLocation(api.getState());
        if (!location.pathname.includes(r.generalFlow.index())) {
          return;
        }
        // reset loadings from price
        api.dispatch(actions.setPriceLoading(false));
        // reset general errors
        api.dispatch(actions.pushNotification(null));
      } catch (error) {
        appUtils.logException('[GFS] Failed to reset loading and notification state', { extra: { error } });
      }
    },
  });

  /**
   * Resets the price retry counter in the flow (same behavior as the former resetRetryAttemptsErrors saga).
   */
  startAppListening({
    type: LOCATION_CHANGE,
    effect: (_action, api) => {
      if (window.location.pathname.includes(r.generalFlow.index())) {
        api.dispatch(actions.resetRetryAttempts());
      }
    },
  });

  /**
   * Closes an open summary sheet after a navigation (same behavior as the former closeSummaryAfterNavigation saga).
   */
  startAppListening({
    type: LOCATION_CHANGE,
    effect: (action, api) => {
      if ((action as LocationChangeAction).payload.isFirstRendering) {
        return;
      }
      const summaryOpen = isSheetOpen(api.getState());
      if (summaryOpen) {
        api.dispatch(actions.toggleSheet());
      }
    },
  });

  /**
   * Scrolls a pushed notification into view, once per burst (same behavior as the former afterPushNotification saga).
   * A cleared notification only cancels a pending scroll: there is nothing to scroll to.
   */
  startAppListening({
    type: GeneralFlowActionTypes.PUSH_NOTIFICATION,
    effect: async (action, api) => {
      api.cancelActiveListeners();
      if (!(action as ReturnType<typeof actions.pushNotification>).payload) {
        return;
      }
      await api.delay(500);
      afterPushNotification();
    },
  });
};
